import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Tooltip, Typography, alpha, useTheme, styled, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import { TrendingUp, Download } from 'lucide-react';
import ChipInputList from 'components/ChipInputList';
import useReports from 'hooks/useReports';
import DashboardFilters from 'StatisticsModule/components/CountStatistics/DashboardFilters';
import { REPORTS_VIEW_TYPES } from '@constants';
import { PageContainer, MinimalCard, CleanButton } from 'components/styled';
import useAppNavigation from 'hooks/useAppNavigation';
import StandardPageHeader from 'components/StandardPageHeader';

// ==================== Styled Components ====================

// PageContainer, MinimalCard, CleanButton now imported from components/styled

// Non-interactive stat card (no hover effects) - matches dark theme styling
const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(24px)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(148, 163, 184, 0.12)'
      : '1px solid rgba(226, 232, 240, 0.8)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
      : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const TabButton = styled(Button)<{ $active?: boolean }>(({ theme, $active }) => ({
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: $active ? 600 : 500,
  color: $active ? theme.palette.text.primary : alpha(theme.palette.text.secondary, 0.7),
  padding: theme.spacing(1, 2),
  minWidth: 'auto',
  borderRadius: theme.spacing(2), // 8px
  background: 'none',
  border: 'none',
  position: 'relative',
  '&:hover': {
    background: alpha(theme.palette.text.primary, 0.05),
    color: theme.palette.text.primary,
  },
  '&::after': $active
    ? {
        content: '""',
        position: 'absolute',
        bottom: -1,
        left: theme.spacing(2),
        right: theme.spacing(2),
        height: 2,
        background: theme.palette.primary.main,
        borderRadius: theme.spacing(0.5), // 2px
      }
    : {},
}));

// CleanButton now imported from components/styled

// ==================== Sub Components ====================

interface StatCardProps {
  label: string;
  value: string | number;
  change?: { value: string; isPositive: boolean };
  sublabel?: string;
}

const StatisticCard = ({ label, value, change, sublabel }: StatCardProps) => {
  const theme = useTheme();

  return (
    <StatCard>
      <Typography
        variant="caption"
        sx={{
          color: alpha(theme.palette.text.secondary, 0.7),
          fontWeight: 500,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </Typography>

      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            fontSize: '2rem',
            lineHeight: 1,
            color: theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
        {sublabel && (
          <Typography
            variant="caption"
            sx={{
              color: alpha(theme.palette.text.secondary, 0.6),
              fontSize: '0.75rem',
              mt: 0.5,
              display: 'block',
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>

      {change && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <TrendingUp
            size={14}
            style={{
              color: change.isPositive ? theme.palette.success.main : theme.palette.error.main,
              transform: change.isPositive ? 'none' : 'rotate(180deg)',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: change.isPositive ? theme.palette.success.main : theme.palette.error.main,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          >
            {change.value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: alpha(theme.palette.text.secondary, 0.6),
              fontSize: '0.75rem',
            }}
          >
            from last week
          </Typography>
        </Box>
      )}
    </StatCard>
  );
};

// ==================== Main Component ====================

// Tabs valid for each view. TEAMS has two tabs ("teams" and "customers")
// that navigate to the same view — both stay valid so an in-session click
// on Customers isn't overwritten when the route param updates.
const VALID_TABS_FOR_VIEW: Record<string, string[]> = {
  [REPORTS_VIEW_TYPES.TEAMS]: ['teams', 'customers'],
  [REPORTS_VIEW_TYPES.DEACTIVATED_TEAMS]: ['deactivated'],
  [REPORTS_VIEW_TYPES.COUNT_STATISTICS]: ['statistics'],
  [REPORTS_VIEW_TYPES.INACTIVE_TEAMS]: ['inactive'],
};

const deriveTabFromView = (view: string): string => {
  return VALID_TABS_FOR_VIEW[view]?.[0] ?? 'teams';
};

const Teams = () => {
  const theme = useTheme();
  const { navigateToTeamsTable } = useAppNavigation();
  const {
    renderChip,
    handleExportClick,
    renderChipInput,
    tags,
    excludeTags,
    renderBody,
    BulkTeamActionsSelector,
    DeactivateTeamsConfirmModal,
    ReactivateTeamsConfirmModal,
    selectAllTeams,
    disableSelectAll,
    reportTableView,
    statisticalCountsProps,
    renderDateRangeFields,
    isExportingTeams,
  } = useReports();

  // Customers and Teams tabs both navigate to REPORTS_VIEW_TYPES.TEAMS, so
  // activeTab is *seeded* from reportTableView for deep links, then tracked
  // locally so clicking Customers can highlight separately from Teams. The
  // effect only overwrites activeTab when it's not valid for the current
  // view — so an in-session click on Customers survives the route param
  // update, but a stale /reports/utilization deep link still resets to the
  // default tab after the redirect.
  const [activeTab, setActiveTab] = useState<string>(deriveTabFromView(reportTableView));

  useEffect(() => {
    const validTabs = VALID_TABS_FOR_VIEW[reportTableView] ?? ['teams'];
    setActiveTab((current) => (validTabs.includes(current) ? current : validTabs[0]));
  }, [reportTableView]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    switch (tab) {
      case 'teams':
        navigateToTeamsTable(REPORTS_VIEW_TYPES.TEAMS);
        break;
      case 'customers':
        navigateToTeamsTable(REPORTS_VIEW_TYPES.TEAMS);
        break;
      case 'deactivated':
        navigateToTeamsTable(REPORTS_VIEW_TYPES.DEACTIVATED_TEAMS);
        break;
      case 'statistics':
        navigateToTeamsTable(REPORTS_VIEW_TYPES.COUNT_STATISTICS);
        break;
      case 'inactive':
        navigateToTeamsTable(REPORTS_VIEW_TYPES.INACTIVE_TEAMS);
        break;
    }
  };

  const showSearchTags =
    reportTableView === REPORTS_VIEW_TYPES.DEACTIVATED_TEAMS ||
    reportTableView === REPORTS_VIEW_TYPES.TEAMS;

  const showDateRangeFilter = reportTableView === REPORTS_VIEW_TYPES.TEAMS;
  const isStatisticsView = reportTableView === REPORTS_VIEW_TYPES.COUNT_STATISTICS;
  const shouldHideSelectorOptions = isStatisticsView;

  // Simplified stats
  const stats = [
    {
      label: 'Active Users',
      value: '815',
      sublabel: 'Oct 31, 2025',
      change: { value: '+12%', isPositive: true },
    },
    {
      label: 'Teams',
      value: '1',
      sublabel: 'Last Week',
      change: { value: '-5%', isPositive: false },
    },
    {
      label: 'Team Members',
      value: '472',
      sublabel: 'Last Week',
      change: { value: '+8%', isPositive: true },
    },
    {
      label: 'Departments',
      value: '5',
      sublabel: 'Last Week',
    },
    {
      label: 'Offices',
      value: '5',
      sublabel: 'Last Week',
    },
    {
      label: 'Roles',
      value: '473',
      sublabel: 'Last Week',
    },
  ];

  return (
    <PageContainer>
      <Helmet title="Reports" />

      <StandardPageHeader
        title="Reports"
        subtitle="Monitor teams, customers, and organizational data"
        icon="Reports"
        customBreadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Reports' },
          {
            label:
              activeTab === 'teams'
                ? 'Teams'
                : activeTab === 'customers'
                  ? 'Customers'
                  : activeTab === 'deactivated'
                    ? 'Deactivated'
                    : activeTab === 'statistics'
                      ? 'Statistics'
                      : 'Teams',
          },
        ]}
        sx={{ mb: 6 }}
      />

      {/* Clean Tabs */}
      <Box
        sx={{
          mb: 4,
        }}
      >
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5'}`,
            display: 'flex',
            gap: 1,
          }}
        >
          <TabButton $active={activeTab === 'teams'} onClick={() => handleTabChange('teams')}>
            Teams
          </TabButton>
          <TabButton
            $active={activeTab === 'customers'}
            onClick={() => handleTabChange('customers')}
          >
            Customers
          </TabButton>
          <TabButton
            $active={activeTab === 'deactivated'}
            onClick={() => handleTabChange('deactivated')}
          >
            Deactivated
          </TabButton>
          <TabButton
            $active={activeTab === 'statistics'}
            onClick={() => handleTabChange('statistics')}
          >
            Statistics
          </TabButton>
        </Box>
      </Box>

      {/* Minimal Statistics Grid */}
      {isStatisticsView && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: 2,
            mb: 4,
          }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <StatisticCard {...stat} />
            </motion.div>
          ))}
        </Box>
      )}

      {/* Clean Controls */}
      {!shouldHideSelectorOptions && (
        <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <CleanButton onClick={selectAllTeams} disabled={disableSelectAll}>
                Select all
              </CleanButton>
              {BulkTeamActionsSelector}
            </Box>
          </Box>
        </MinimalCard>
      )}

      {/* Statistics Filters */}
      {isStatisticsView && (
        <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
          <DashboardFilters
            isLoading={statisticalCountsProps.isFetchingCountStatistic}
            teamCreationDateOption={statisticalCountsProps.teamCreationDateOption}
            onSelectSearchRangeDate={statisticalCountsProps.onSelectSearchRangeDate}
            onSelectTeamCreationDate={statisticalCountsProps.onSelectTeamCreationDate}
            selectedSearchDateRange={statisticalCountsProps.selectedSearchDateRange}
            onSelectCustomSearchRange={statisticalCountsProps.onSelectCustomSearchRange}
            tempCustomStart={statisticalCountsProps.tempCustomStart}
            tempCustomEnd={statisticalCountsProps.tempCustomEnd}
            triggerCountStatisticsFilter={statisticalCountsProps.triggerCountStatisticsFilter}
          />
        </MinimalCard>
      )}

      {/* Modals */}
      {DeactivateTeamsConfirmModal}
      {ReactivateTeamsConfirmModal}

      {/* Filters Section */}
      {(showDateRangeFilter || showSearchTags) && (
        <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
          {/* Date Range Filter */}
          {showDateRangeFilter && (
            <Box
              sx={{
                mb: showSearchTags ? 3 : 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: '0.875rem',
                  }}
                >
                  Date Range
                </Typography>
                <Tooltip
                  title={
                    isExportingTeams ? 'Export in progress' : 'Export teams (excludes test tags)'
                  }
                  arrow
                >
                  <span>
                    <CleanButton
                      variant="contained"
                      onClick={handleExportClick}
                      disabled={isExportingTeams}
                      startIcon={
                        isExportingTeams ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: 'linear',
                            }}
                          >
                            <Download size={16} />
                          </motion.div>
                        ) : (
                          <Download size={16} />
                        )
                      }
                    >
                      {isExportingTeams ? 'Exporting...' : 'Export'}
                    </CleanButton>
                  </span>
                </Tooltip>
              </Box>
              {renderDateRangeFields()}
            </Box>
          )}

          {/* Divider between sections */}
          {showDateRangeFilter && showSearchTags && <Divider sx={{ my: 3 }} />}

          {/* Tag Filters */}
          {showSearchTags && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: '0.875rem',
                }}
              >
                Filter by Tags
              </Typography>
              <ChipInputList
                renderChipInput={renderChipInput}
                renderChip={renderChip}
                chipsToInclude={tags}
                chipsToExclude={excludeTags}
              />
            </Box>
          )}
        </MinimalCard>
      )}

      {/* Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <MinimalCard $hoverEffect={false}>{renderBody()}</MinimalCard>
      </motion.div>
    </PageContainer>
  );
};

export default Teams;
